import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import Card from '../components/shared/Card.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import { fileLinkClick } from '../utils/openFile.js';

// Search results from the header: tasks, processes, documents, members, meetings and
// decisions — only what the member may open (the server leaves out the rest).

const queryFromUrl = () => (new URLSearchParams(window.location.search).get('q') || '').trim();
const fmtDate = v => (v ? new Date(`${v.slice(0, 10)}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '');

export default function SearchView({ url }) {
  const q = queryFromUrl();
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setResults(null); setError(null);
    if (q.length < 2) { setResults({}); return; }
    api(`/api/search?q=${encodeURIComponent(q)}`).then(setResults).catch(() => setError('Zoeken lukte niet. Probeer het opnieuw.'));
  }, [url, q]);

  const KINDS = ['tasks', 'processes', 'documents', 'members', 'meetings', 'decisions'];
  const total = results ? KINDS.reduce((n, k) => n + (results[k]?.length || 0), 0) : 0;
  const row = 'display:flex;gap:10px;align-items:center;justify-content:space-between;flex-wrap:wrap;padding:8px 0;border-bottom:1px solid var(--color-border);text-decoration:none;color:inherit';
  const fmtWhen = v => (v ? new Date(v).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
  return (
    <div style="max-width:820px">
      <div style="margin-bottom:16px">
        <h1 class="page-title">🔍 Zoeken</h1>
        <div class="text-sm text-muted">{q ? <>Resultaten voor “{q}”</> : 'Typ minstens twee tekens in de zoekbalk bovenin.'}</div>
      </div>
      {error && <div class="alert alert-error">{error}</div>}
      {!results && !error && <div class="text-muted" style="padding:40px;text-align:center">Zoeken…</div>}
      {results && q.length >= 2 && total === 0 && (
        <div class="text-muted" style="padding:24px 0">Niets gevonden. Probeer een ander woord.</div>
      )}
      <div style="display:flex;flex-direction:column;gap:16px">
        {results?.tasks?.length > 0 && (
          <Card title={`Taken (${results.tasks.length})`}>
            {results.tasks.map(t => (
              <a key={t.id} href={`/tasks/${t.id}`} style="display:flex;gap:10px;align-items:center;justify-content:space-between;flex-wrap:wrap;padding:8px 0;border-bottom:1px solid var(--color-border);text-decoration:none;color:inherit">
                <span style="min-width:0;flex:1;overflow-wrap:anywhere">
                  <strong>{t.title}</strong>
                  <span class="text-sm text-muted" style="display:block">{[t.owner_name, t.deadline && `deadline ${fmtDate(t.deadline)}`].filter(Boolean).join(' · ') || 'Zonder eigenaar'}</span>
                </span>
                <StatusBadge status={t.status} type="task" />
              </a>
            ))}
          </Card>
        )}
        {results?.processes?.length > 0 && (
          <Card title={`Processen (${results.processes.length})`}>
            {results.processes.map(p => (
              <a key={p.id} href={`/processes/${p.id}`} style="display:flex;gap:10px;align-items:center;justify-content:space-between;flex-wrap:wrap;padding:8px 0;border-bottom:1px solid var(--color-border);text-decoration:none;color:inherit">
                <span style="min-width:0;flex:1;overflow-wrap:anywhere">
                  <strong>{p.title}</strong>
                  {p.started_by_name && <span class="text-sm text-muted" style="display:block">gestart door {p.started_by_name}</span>}
                </span>
                <StatusBadge status={p.status} />
              </a>
            ))}
          </Card>
        )}
        {results?.members?.length > 0 && (
          <Card title={`Leden (${results.members.length})`}>
            {results.members.map(m => (
              <a key={m.id} href={`/profile/${m.id}`} style={row}>
                <span style="min-width:0;flex:1;overflow-wrap:anywhere">
                  <strong>{m.display_name}</strong>
                  {m.functions && <span class="text-sm text-muted" style="display:block">{m.functions.split(',').join(' · ')}</span>}
                </span>
              </a>
            ))}
          </Card>
        )}
        {results?.documents?.length > 0 && (
          <Card title={`Documenten (${results.documents.length})`}>
            {results.documents.map(d => (
              <a key={d.id} href={d.is_external ? (d.external_url || '/documents') : `/api/documents/${d.id}/download`} target="_blank" rel="noopener noreferrer" style={row}
                onClick={d.is_external ? undefined : e => fileLinkClick(e, d.file_name)}>
                <span style="min-width:0;flex:1;overflow-wrap:anywhere">
                  <strong>{d.is_external ? '🔗' : '📄'} {d.file_name}</strong>
                  {d.folder_name && <span class="text-sm text-muted" style="display:block">📁 {d.folder_name}</span>}
                </span>
              </a>
            ))}
          </Card>
        )}
        {results?.meetings?.length > 0 && (
          <Card title={`Vergaderingen (${results.meetings.length})`}>
            {results.meetings.map(m => (
              <a key={m.id} href={`/meetings/${m.id}`} style={row}>
                <span style="min-width:0;flex:1;overflow-wrap:anywhere">
                  <strong>{m.title}</strong>
                  <span class="text-sm text-muted" style="display:block">{m.body_name} · {fmtWhen(m.starts_at)}</span>
                </span>
              </a>
            ))}
          </Card>
        )}
        {results?.decisions?.length > 0 && (
          <Card title={`Besluiten (${results.decisions.length})`}>
            {results.decisions.map(d => (
              <a key={d.id} href={`/meetings/${d.meeting_id}`} style={row}>
                <span style="min-width:0;flex:1;overflow-wrap:anywhere">
                  <strong>{d.number}</strong> {d.text.length > 140 ? `${d.text.slice(0, 140)}…` : d.text}
                  <span class="text-sm text-muted" style="display:block">{d.body_name} · {d.meeting_title}</span>
                </span>
              </a>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

